import AuditInfo from 'components/audit/AuditInfo';
import { RecordPage } from 'components/record-header/RecordHeader';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { useGetIdentificationPanelQuery } from 'store/api/client-identifications';
import { Contract } from 'types/contracts';
import { useClassifiers } from 'utils/useClassifiers';
import { ClientIdentificationForm } from './ClientIdentificationForm';
import { ClientIdentificationHeader } from './ClientIdentificationHeader';
import { ClientIdentificationNotices } from './ClientIdentificationNotices';
import { TIER_CLASSIFIER } from './tierOptions';

export const CLIENT_IDENTIFICATION_CLASSIFIERS = [
    'CONTRACT_TYPE',
    TIER_CLASSIFIER,
    'SOURCE_OF_FUNDS',
    'EMPLOYMENT_STATUS',
    'ACTING_FOR_CLIENT_ROLE',
    'COUNTRY',
];

interface Props {
    contract: Contract;
}

/** A client identification's page: the header with its actions, the standing notice of its state and the form. */
export function ClientIdentificationPage({ contract }: Props) {
    const isNew = useIsNewDesign();
    const assessment = contract.assessment;
    const { data: panel } = useGetIdentificationPanelQuery(contract.id);
    const { classifiers } = useClassifiers({ codes: CLIENT_IDENTIFICATION_CLASSIFIERS });

    if (!assessment) {
        return null;
    }
    const header = (
        <ClientIdentificationHeader
            contract={contract}
            assessment={assessment}
            panel={panel}
            classifiers={classifiers}
        />
    );
    const notices = <ClientIdentificationNotices assessment={assessment} />;
    const form = <ClientIdentificationForm contract={contract} assessment={assessment} classifiers={classifiers} />;
    const auditInfo = (
        <AuditInfo
            createdAt={contract.createdAt}
            createdByEmployee={contract.createdByEmployee}
            lastModifiedAt={contract.lastModifiedAt}
            changedByEmployee={contract.changedByEmployee}
        />
    );

    if (isNew) {
        return (
            <RecordPage
                header={
                    <div className="flex flex-col gap-4">
                        {header}
                        {notices}
                    </div>
                }
                tabbed={false}
            >
                <div className="flex flex-col gap-4">
                    {form}
                    {auditInfo}
                </div>
            </RecordPage>
        );
    }
    return (
        // The header stays put and the blocks scroll under it, as on the new design's record page.
        <PaddedLayout className="sm:flex sm:h-full sm:flex-col sm:overflow-hidden">
            <div className="sm:flex-none">{header}</div>
            {/* The gap under the header is the scroll area's padding, which the side panel stays below as it sticks. */}
            <div className="flex flex-col gap-4 pt-4 pb-3 sm:min-h-0 sm:flex-1 sm:overflow-y-auto">
                {notices}
                {form}
                {auditInfo}
            </div>
        </PaddedLayout>
    );
}
